import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import {
  PublicDemoPage,
  PublicEventDetailPage,
  PublicEventsPage,
  PublicSchedulePage,
  PublicTopPage
} from "../pages/public/PublicPages";
import {
  FamilyHomePage,
  FamilyMemberCommunicationDetailPage,
  FamilyMemberCommunicationsPage,
  FamilyOccurrenceAbsencePage,
  FamilySchedulePage,
  FamilyVideoDetailPage,
  FamilyVideosPage,
  NotificationsPage,
  StudentDetailPage,
  StudentBillingPaymentMethodPage
} from "../pages/family/FamilyPages";
import { TeacherAbsenceRequestPage, TeacherHomePage, TeacherOccurrenceDetailPage } from "../pages/teacher/TeacherPages";
import {
  AdminEnrollmentsPage,
  AdminEventsPage,
  AdminFamilyAccountCreatePage,
  AdminFamilyAccountDetailPage,
  AdminFamilyAccountsPage,
  AdminBillingPage,
  AdminBillingLessonFeesPage,
  AdminBillingLessonFeeDetailPage,
  AdminBillingEventFeesPage,
  AdminBillingEventFeeDetailPage,
  AdminBillingManualItemsPage,
  AdminBillingManualItemDetailPage,
  AdminBillingMonthlyPage,
  AdminBillingMonthlyStudentPage,
  AdminBillingCardInvoicesPage,
  AdminBulkEnrollmentCreatePage,
  AdminContractPage,
  AdminContractCancellationPage,
  AdminContractCancellationRevocationPage,
  AdminGuardianLinkRequestsPage,
  AdminGuardianLinksPage,
  AdminHomePage,
  AdminIntegrationsPage,
  AdminLessonDetailPage,
  AdminLessonsPage,
  AdminMemberCommunicationsPage,
  AdminMembersPage,
  AdminOccurrenceDetailPage,
  AdminPoliciesPage,
  AdminPublicEventsPage,
  AdminPublicSettingsPage,
  AdminRecurringSlotDetailPage,
  AdminRecurringSlotsPage,
  AdminScheduleEntryPage,
  AdminScheduleMonthPage,
  AdminStudentCreatePage,
  AdminStudentDetailPage,
  AdminTenantSettingsPage,
  AdminStudentsPage,
  AdminVideosPage
} from "../pages/admin/AdminPages";
import {
  ContactSettingsPage,
  ContractEmailChangeActionPage,
  ContractCancellationActionPage,
  ContractCancellationRevocationActionPage,
  EntryPage,
  LinkStudentConfirmPage,
  MemberInvitePage,
  LinkStudentPage,
  LinkStudentRequestPage,
  NotFoundPage,
  RootLandingPage,
  StudentInvitePage,
  SupportPage,
  TenantServiceEndedPage,
  ReviewDebugLineSubPage
} from "../pages/common/CommonPages";
import { getEnv, isProductionAppEnv } from "../shared/lib/env";
import { readLiffStateReturnTo } from "../shared/lib/liff";

function isRestorableLiffReturnPath(value: string): boolean {
  return /^\/t\/[^/]+(?:\/.*)?$/.test(value);
}

function RootRoute() {
  const env = getEnv();
  const location = useLocation();
  const liffStateReturnTo = readLiffStateReturnTo(location.search);

  if (liffStateReturnTo && isRestorableLiffReturnPath(liffStateReturnTo)) {
    return <p style={{ padding: 16 }}>LINEログイン後の画面へ戻っています。</p>;
  }

  if (isProductionAppEnv(env.appEnv)) {
    return <RootLandingPage />;
  }

  return <Navigate to="/t/demo-club" replace />;
}

export function AppRouter() {
  const env = getEnv();

  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/contract-actions/email-change" element={<ContractEmailChangeActionPage />} />
      <Route path="/contract-actions/cancellation" element={<ContractCancellationActionPage />} />
      <Route path="/contract-actions/cancellation-revocation" element={<ContractCancellationRevocationActionPage />} />

      <Route path="/t/:tenantSlug" element={<PublicTopPage />} />
      <Route path="/t/:tenantSlug/demo" element={<PublicDemoPage />} />
      <Route path="/t/:tenantSlug/public/schedule" element={<PublicSchedulePage />} />
      <Route path="/t/:tenantSlug/public/events" element={<PublicEventsPage />} />
      <Route path="/t/:tenantSlug/public/events/:eventId" element={<PublicEventDetailPage />} />
      <Route path="/t/:tenantSlug/schedule" element={<PublicSchedulePage />} />
      <Route path="/t/:tenantSlug/events" element={<PublicEventsPage />} />
      <Route path="/t/:tenantSlug/events/:eventId" element={<PublicEventDetailPage />} />

      <Route path="/t/:tenantSlug/entry" element={<EntryPage />} />
      <Route path="/t/:tenantSlug/member-invite" element={<MemberInvitePage />} />
      <Route path="/t/:tenantSlug/student-invite" element={<StudentInvitePage />} />
      <Route path="/t/:tenantSlug/link-student" element={<LinkStudentPage />} />
      <Route path="/t/:tenantSlug/link-student/confirm" element={<LinkStudentConfirmPage />} />
      <Route path="/t/:tenantSlug/link-student/request" element={<LinkStudentRequestPage />} />
      <Route path="/t/:tenantSlug/settings/contact" element={<ContactSettingsPage />} />
      <Route path="/t/:tenantSlug/support" element={<SupportPage />} />
      <Route path="/t/:tenantSlug/service-ended" element={<TenantServiceEndedPage />} />
      {env.reviewDebugEnabled ? (
        <Route path="/t/:tenantSlug/debug/line-sub" element={<ReviewDebugLineSubPage />} />
      ) : null}

      <Route path="/t/:tenantSlug/home" element={<FamilyHomePage />} />
      <Route path="/t/:tenantSlug/occurrences/:occurrenceId/absence" element={<FamilyOccurrenceAbsencePage />} />
      <Route path="/t/:tenantSlug/my-schedule" element={<FamilySchedulePage />} />
      <Route path="/t/:tenantSlug/students/:studentId" element={<StudentDetailPage />} />
      <Route path="/t/:tenantSlug/member-communications" element={<FamilyMemberCommunicationsPage />} />
      <Route path="/t/:tenantSlug/member-communications/:communicationId" element={<FamilyMemberCommunicationDetailPage />} />
      <Route path="/t/:tenantSlug/videos" element={<FamilyVideosPage />} />
      <Route path="/t/:tenantSlug/videos/:videoId" element={<FamilyVideoDetailPage />} />
      <Route path="/t/:tenantSlug/notifications" element={<NotificationsPage />} />
      <Route path="/t/:tenantSlug/billing" element={<StudentBillingPaymentMethodPage />} />
      <Route path="/t/:tenantSlug/billing/payment-method" element={<StudentBillingPaymentMethodPage />} />
      <Route path="/t/:tenantSlug/billing/payment-method/cancel" element={<StudentBillingPaymentMethodPage />} />

      <Route path="/t/:tenantSlug/teacher" element={<TeacherHomePage />} />
      <Route path="/t/:tenantSlug/teacher/occurrences/:occurrenceId" element={<TeacherOccurrenceDetailPage />} />
      <Route path="/t/:tenantSlug/teacher/absence-request/new" element={<TeacherAbsenceRequestPage />} />
      <Route path="/t/:tenantSlug/teacher/admin" element={<AdminHomePage />} />
      <Route path="/t/:tenantSlug/teacher/admin/students" element={<AdminStudentsPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/students/new" element={<AdminStudentCreatePage />} />
      <Route path="/t/:tenantSlug/teacher/admin/family-accounts" element={<AdminFamilyAccountsPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/family-accounts/new" element={<AdminFamilyAccountCreatePage />} />
      <Route
        path="/t/:tenantSlug/teacher/admin/family-accounts/:familyAccountId"
        element={<AdminFamilyAccountDetailPage />}
      />
      <Route path="/t/:tenantSlug/teacher/admin/students/:studentId" element={<AdminStudentDetailPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/members" element={<AdminMembersPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/guardian-links" element={<AdminGuardianLinksPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/guardian-link-requests" element={<AdminGuardianLinkRequestsPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/lessons" element={<AdminLessonsPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/lessons/:lessonId" element={<AdminLessonDetailPage />} />
      <Route
        path="/t/:tenantSlug/teacher/admin/lessons/:lessonId/recurring-slots/new"
        element={<AdminRecurringSlotDetailPage />}
      />
      <Route
        path="/t/:tenantSlug/teacher/admin/lessons/:lessonId/recurring-slots/:slotId"
        element={<AdminRecurringSlotDetailPage />}
      />
      <Route path="/t/:tenantSlug/teacher/admin/recurring-slots" element={<AdminRecurringSlotsPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/enrollments" element={<AdminEnrollmentsPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/enrollments/bulk-create" element={<AdminBulkEnrollmentCreatePage />} />
      <Route path="/t/:tenantSlug/teacher/admin/schedule" element={<AdminScheduleEntryPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/schedule-months/:yyyymm" element={<AdminScheduleMonthPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/occurrences/:occurrenceId" element={<AdminOccurrenceDetailPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/events" element={<AdminEventsPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/member-communications" element={<AdminMemberCommunicationsPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/videos" element={<AdminVideosPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/tenant" element={<AdminTenantSettingsPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/public-settings" element={<AdminPublicSettingsPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/public-events" element={<AdminPublicEventsPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/policies" element={<AdminPoliciesPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/integrations" element={<AdminIntegrationsPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/billing" element={<AdminBillingPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/billing/lesson-fees" element={<AdminBillingLessonFeesPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/billing/lesson-fees/:lessonId" element={<AdminBillingLessonFeeDetailPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/billing/event-fees" element={<AdminBillingEventFeesPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/billing/event-fees/:eventId" element={<AdminBillingEventFeeDetailPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/billing/manual-items" element={<AdminBillingManualItemsPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/billing/manual-items/:batchId" element={<AdminBillingManualItemDetailPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/billing/months/:serviceMonth" element={<AdminBillingMonthlyPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/billing/months/:serviceMonth/card-invoices" element={<AdminBillingCardInvoicesPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/billing/months/:serviceMonth/students/:studentId" element={<AdminBillingMonthlyStudentPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/contract" element={<AdminContractPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/contract/cancellation" element={<AdminContractCancellationPage />} />
      <Route path="/t/:tenantSlug/teacher/admin/contract/cancellation/:cancellationRequestId/revoke" element={<AdminContractCancellationRevocationPage />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
